import type { Item } from '../lib/types';

interface Props {
  item: Item;
  onClose: () => void;
}

/** ライブ配信のチャットを持てるソースか。 */
export function isChatCapable(item: Item): boolean {
  return (
    (item.source.provider === 'twitch' && item.source.kind === 'channel') ||
    (item.source.provider === 'youtube' && item.source.kind === 'video')
  );
}

/**
 * チャット表示。同時に 1 つだけ開く。
 *
 * 読み書きとも純正の埋め込みに任せる。このアプリは公式 API を一切呼ばない。
 *
 * 以前はこのアプリ自身の投稿欄 (OAuth + 公式 API) を併設していたが、埋め込み側の
 * 入力欄がそのまま使えており二重になっていたので撤去した。あわせて OAuth と
 * サーバ側の API 中継も丸ごと無くなり、管理するシークレットがゼロになった。
 *
 * 見出し行は置かない。狭い画面でチャットの高さを削るだけで情報量がないため、
 * 閉じるボタンだけを映像の上に重ねる。
 *
 * ## Twitch の上位ギフター（リーダーボード）
 *
 * **取得の段階では消せない。** 埋め込みチャットはクロスオリジンの iframe なので
 * 中の DOM には触れられず、公式の埋め込みパラメータは `parent` と `darkpopout` だけで
 * リーダーボードを止めるものはない。表示可否は配信者側の設定で、視聴者からは変えられない。
 * DOM を書き換える手段（拡張機能相当のことをアプリ側でやる）は非公式操作なので採らない。
 *
 * そこで CSS で上端を切り落とす。`.chat-frame-clip` を参照。
 *
 * ## ダークテーマ
 *
 * YouTube の `live_chat` は `dark_theme=1` を付けないと背景が白のまま描画されることがあり、
 * 文字色だけダーク用（白）になって読めなくなる。Twitch 側は `darkpopout` で同じことをしている。
 */
export function ChatPanel({ item, onClose }: Props) {
  const twitchChannel =
    item.source.provider === 'twitch' && item.source.kind === 'channel' ? item.source.channel : null;
  const ytVideoId =
    item.source.provider === 'youtube' && item.source.kind === 'video' ? item.source.videoId : null;

  return (
    <div className="chat-wrap">
      {twitchChannel ? (
        <div className="chat-frame-clip" data-trim="twitch">
          <iframe
            className="chat-frame"
            title={`Twitch chat ${twitchChannel}`}
            src={`https://www.twitch.tv/embed/${encodeURIComponent(twitchChannel)}/chat?parent=${encodeURIComponent(
              window.location.hostname,
            )}&darkpopout`}
            referrerPolicy="strict-origin-when-cross-origin"
          />
        </div>
      ) : ytVideoId ? (
        <div className="chat-frame-clip">
          <iframe
            className="chat-frame"
            title="YouTube live chat"
            src={`https://www.youtube.com/live_chat?v=${encodeURIComponent(ytVideoId)}&embed_domain=${encodeURIComponent(
              window.location.hostname,
            )}&dark_theme=1`}
            referrerPolicy="strict-origin-when-cross-origin"
          />
        </div>
      ) : (
        <div className="chat-note">この配信ではチャットを表示できません。</div>
      )}

      <button className="chat-close" onClick={onClose} aria-label="チャットを閉じる">
        ✕
      </button>
    </div>
  );
}
